import { Box, Theme, Typography } from "@outschool/backpack";
import { ActivityRatingAndReviewsQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  AttachmentGallery,
  isImageAttachment,
} from "@outschool/ui-components-shared";
import { Screen, pxToRem } from "@outschool/ui-utils";
import React, { useMemo } from "react";

import { ReviewCaption } from "../index";

type ReviewWithAttachment =
  ActivityRatingAndReviewsQuery["activity"]["reviewsWithAttachments"][number];

type Attachment = ReviewWithAttachment["attachments"][number];

type Props = {
  reviewsWithAttachments: ReviewWithAttachment[];
};

export default function ParentSubmittedPhotos({
  reviewsWithAttachments,
}: Props) {
  const { t } = useTranslation(
    "ui-components-website\\src\\Reviews\\ParentSubmittedPhotos",
  );
  const isMobile = Screen.useIsMobile();

  const photos = useMemo(() => {
    return reviewsWithAttachments.reduce((acc, { attachments }) => {
      for (const attachment of attachments) {
        if (isImageAttachment(attachment)) {
          acc.push(attachment);
        }
      }

      return acc;
    }, [] as Attachment[]);
  }, [reviewsWithAttachments]);

  const reviewsByAttachment = useMemo(() => {
    return reviewsWithAttachments.reduce((acc, review) => {
      for (const attachment of review.attachments) {
        acc[attachment.uid] = review;
      }

      return acc;
    }, {} as Record<string, ReviewWithAttachment>);
  }, [reviewsWithAttachments]);

  const attachments = isMobile ? photos.slice(0, 3) : photos.slice(0, 5);

  return (
    <Box
      sx={(theme: Theme) => ({
        marginY: theme.spacing(12),

        [theme.breakpoints.up("md")]: {
          marginY: theme.spacing(24),
        },
      })}
    >
      <Typography
        variant="subtitle1"
        sx={(theme: Theme) => ({
          color: "grey.600",
          marginBottom: theme.spacing(8),
        })}
      >
        {t("Parent-submitted images")}
      </Typography>
      <AttachmentGallery
        attachments={attachments}
        captionForAttachment={attachment => (
          <ReviewCaption review={reviewsByAttachment[attachment.uid]} />
        )}
        numPrimaryAttachments={0}
        numSecondaryAttachments={isMobile ? 3 : 5}
        secondaryAttachmentMaxHeight={isMobile ? pxToRem(108) : pxToRem(300)}
      />
    </Box>
  );
}
